import { Suspense, lazy, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { HugeiconsIcon } from '@hugeicons/react';
import { Menu01Icon, Road01Icon, Target01Icon, BalanceScaleIcon } from '@hugeicons/core-free-icons';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import Registry from '@/pages/Registry';

// Las otras tres vistas del portafolio se cargan bajo demanda: solo se montan
// cuando el usuario abre su pestaña.
const Roadmap = lazy(() => import('@/pages/Roadmap'));
const Tracking = lazy(() => import('@/pages/Tracking'));
const Prioritization = lazy(() => import('@/pages/Prioritization'));

// Pestañas del hub de portafolio. Cada una es una "lente" sobre el mismo
// conjunto de iniciativas. `route` es la ruta canónica usada para el control de
// acceso: si el usuario no tiene permiso a esa ruta, la pestaña no se muestra.
// La pestaña "Tabla" es /registro (siempre visible para quien llega aquí).
const TABS = [
  { value: 'tabla', label: 'Tabla', icon: Menu01Icon, route: '/registro' },
  { value: 'roadmap', label: 'Roadmap', icon: Road01Icon, route: '/roadmap' },
  { value: 'tracking', label: 'Tracking', icon: Target01Icon, route: '/tracking' },
  { value: 'priorizacion', label: 'Priorización', icon: BalanceScaleIcon, route: '/priorizacion' },
];

// Contenedor de carga diferida para las sub-páginas.
function Lazy({ children }) {
  return (
    <Suspense
      fallback={
        <div className="mx-auto max-w-[1400px] px-6 py-10">
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      }>
      {children}
    </Suspense>
  );
}

// PortfolioHub agrupa en un solo lugar las vistas del portafolio de iniciativas.
// La vista activa se refleja en la URL (?vista=) para enlaces compartibles.
// Las pestañas se filtran por permiso: un usuario sin acceso a /roadmap,
// /tracking o /priorizacion no ve esa pestaña ni su contenido.
export default function PortfolioHub() {
  const { accessibleRoutes, hasAccess } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Pestañas visibles según permisos. Mientras los permisos no han cargado
  // (accessibleRoutes vacío), se muestran todas para no parpadear.
  const tabs = useMemo(() => {
    if (!accessibleRoutes || accessibleRoutes.length === 0) return TABS;
    return TABS.filter((t) => t.value === 'tabla' || hasAccess(t.route));
  }, [accessibleRoutes, hasAccess]);

  const valid = useMemo(() => new Set(tabs.map((t) => t.value)), [tabs]);
  const raw = searchParams.get('vista');
  const active = valid.has(raw) ? raw : 'tabla';

  const onChange = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'tabla') next.delete('vista');
    else next.set('vista', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Tabs value={active} onValueChange={onChange}>
      {/* La barra de pestañas comparte el mismo ancho/padding que el contenido
          de las páginas (max-w-[1400px] px-6) para que quede alineada. */}
      <div className="mx-auto max-w-[1400px] px-6 pt-8">
        <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-xl bg-muted/50 p-1 sm:w-fit">
          {tabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value} className="gap-1.5 whitespace-nowrap px-3 py-1.5">
              <HugeiconsIcon icon={t.icon} className="size-4" aria-hidden="true" />
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      {/* Cada TabsContent monta la página completa, que ya trae su propio
          contenedor con max-w-[1400px]/max-w-7xl y padding. No añadimos padding
          extra aquí para no descuadrar. */}
      <TabsContent value="tabla" className="mt-0">
        <Registry />
      </TabsContent>

      {valid.has('roadmap') && (
        <TabsContent value="roadmap" className="mt-0">
          <Lazy>{active === 'roadmap' && <Roadmap />}</Lazy>
        </TabsContent>
      )}

      {valid.has('tracking') && (
        <TabsContent value="tracking" className="mt-0">
          <Lazy>{active === 'tracking' && <Tracking />}</Lazy>
        </TabsContent>
      )}

      {valid.has('priorizacion') && (
        <TabsContent value="priorizacion" className="mt-0">
          <Lazy>{active === 'priorizacion' && <Prioritization />}</Lazy>
        </TabsContent>
      )}
    </Tabs>
  );
}
